
import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { ContactFormInput } from "./ContactFormInput";
import { ContactFormSubmitButton } from "./ContactFormSubmitButton";
import { ContactFormPrivacyPolicy } from "./ContactFormPrivacyPolicy";
import { useEmailSender } from "@/hooks/useEmailSender";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    contactConsent: false // Consent to be contacted
  });
  
  const [consentError, setConsentError] = useState(false);
  const { sendEmail, isSubmitting } = useEmailSender();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleConsentChange = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      contactConsent: checked
    }));
    
    // Clear the consent error when user checks the box
    if (checked) {
      setConsentError(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if consent was given
    if (!formData.contactConsent) {
      setConsentError(true);
      return;
    }
    
    // Get the privacy policy URL for the email template
    const privacyPolicyUrl = window.location.origin + "/privacidad";
    
    // Add privacy policy URL to the form data
    const formDataWithPrivacyPolicy = {
      ...formData,
      privacyPolicyUrl
    };
    
    const success = await sendEmail(formDataWithPrivacyPolicy);
    
    if (success) {
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        contactConsent: false
      });
    }
  };

  return (
    <div className="bg-white p-8 rounded-lg shadow-lg border border-[#e0f2fe]">
      <h3 className="text-2xl font-medium mb-6 text-[#0c4a6e]">Envíanos un mensaje</h3>
      <form 
        onSubmit={handleSubmit}
        className="space-y-6"
        noValidate
      >
        <ContactFormInput
          id="name"
          name="name"
          label="Nombre completo"
          placeholder="Tu nombre"
          value={formData.name}
          onChange={handleChange}
          required
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ContactFormInput
            id="email"
            name="email"
            label="Correo electrónico"
            type="email"
            placeholder="tu.correo@ejemplo.com"
            value={formData.email}
            onChange={handleChange}
            required
          />
          
          <ContactFormInput
            id="phone"
            name="phone"
            label="Teléfono"
            type="tel"
            placeholder="(123) 456-7890"
            value={formData.phone}
            onChange={handleChange}
          />
        </div>
        
        <div>
          <label htmlFor="message" className="block text-sm font-medium mb-2 text-[#0c4a6e]">
            Mensaje
          </label>
          <Textarea
            id="message"
            name="message"
            placeholder="¿Cómo podemos ayudarte?"
            value={formData.message}
            onChange={handleChange}
            required
            className="w-full h-32 rounded-md resize-none border-[#bae6fd]"
          />
        </div>
        
        {consentError && (
          <Alert variant="destructive" className="bg-red-50 border-red-200">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="ml-2">
              Es necesario otorgar su consentimiento para enviar el formulario.
            </AlertDescription>
          </Alert>
        )}
        
        <div className={`flex items-start space-x-3 p-4 rounded-md border ${consentError ? 'bg-red-50 border-red-200' : 'bg-[#f0f9ff] border-[#bae6fd]'}`}>
          <Checkbox 
            id="contact-consent" 
            checked={formData.contactConsent}
            onCheckedChange={handleConsentChange}
            className={`mt-1 ${consentError ? 'border-red-400' : ''}`}
            required
            aria-required="true"
            aria-invalid={consentError}
            aria-errormessage={consentError ? "consent-error" : undefined}
            title="Por favor marque esta casilla para continuar."
          />
          <Label 
            htmlFor="contact-consent" 
            className={`text-sm ${consentError ? 'text-red-600 font-medium' : 'text-gray-700 font-normal'}`}
          >
            Al enviar este formulario, otorgo mi consentimiento para que GAV Insurance me contacte por correo electrónico o teléfono respecto a mi solicitud. Entiendo que puedo retirar mi consentimiento en cualquier momento.
          </Label>
        </div>
        
        <ContactFormSubmitButton isSubmitting={isSubmitting} />
      </form>
      
      <ContactFormPrivacyPolicy />
    </div>
  );
};
